import { useEffect, useState } from 'react';
import { hasScope } from '@nyce/admin-kit/api';
import { SCOPES, type Environment } from '../api';
import { api, useHubLogin } from '../session';
import { loadTargets, targetSession, useTargets } from '../identity/session';
import type { Tenant } from '../identity/api';
import { loadAuthDirectory } from '../pages/statistics/links';

/**
 * Where print-queue tenants meet AUTH and the registry: the print queue only knows its `TenantId`, either a free-text
 * name an AUTH tenant claims with the alias `printqueue:<TenantId>` (any case; tenants from before AUTH) or, for a
 * tenant without alias, the AUTH tenant's own id (what the print queue uses for its AUTH callers); a registry
 * environment names its tenant (`authTenantId`). Read from that environment's AUTH through the identity proxy, so it
 * needs `ophub:auth:read` and a session there; without them ids simply stay unlinked.
 */
export interface PrintQueueLinks {
  /** Why nothing could be linked (no AUTH access, signed out, failure). */
  reason: string | null;
  /** lower-cased print-queue TenantId (alias value or AUTH tenant id) → AUTH tenant. */
  tenants: Map<string, Tenant>;
  /** AUTH tenant id (lower case) → its registry environments. */
  environments: Map<string, Environment[]>;
  customerNames: Map<string, string>;
}

const EMPTY: PrintQueueLinks = {
  reason: null,
  tenants: new Map(),
  environments: new Map(),
  customerNames: new Map(),
};

export const pqKey = (tenantId: string) => tenantId.trim().toLowerCase();

async function load(target: string, canAuth: boolean): Promise<PrintQueueLinks> {
  if (!canAuth) return { ...EMPTY, reason: 'Linking tenants needs ophub:auth:read.' };
  await loadTargets();
  if (!targetSession(target).isSignedIn()) return { ...EMPTY, reason: `Not signed in to AUTH ${target}.` };
  const [{ customers, tenants }, envs] = await Promise.all([
    loadAuthDirectory(target),
    api.environments().catch(() => [] as Environment[]),
  ]);
  const links: PrintQueueLinks = {
    reason: null,
    tenants: new Map(),
    environments: new Map(),
    customerNames: new Map(customers.map((c) => [c.id, c.name])),
  };
  for (const t of tenants)
    for (const a of t.aliases ?? []) if (a.system === 'printqueue') links.tenants.set(pqKey(a.value), t);
  // a tenant without alias is its AUTH id in the print queue; an alias wins
  for (const t of tenants) if (!links.tenants.has(pqKey(t.id))) links.tenants.set(pqKey(t.id), t);
  for (const e of envs)
    if (e.authTenantId) {
      const k = e.authTenantId.toLowerCase();
      links.environments.set(k, [...(links.environments.get(k) ?? []), e]);
    }
  return links;
}

const TTL = 120_000;
const cache = new Map<string, { at: number; key: string; data: Promise<PrintQueueLinks> }>();

/** The link directory of one environment, cached for 2 minutes. */
export function usePrintQueueLinks(target: string): PrintQueueLinks & { loading: boolean } {
  const canAuth = hasScope(useHubLogin()?.scopes, SCOPES.authRead);
  const { signedIn } = useTargets();
  const key = `${canAuth}|${signedIn(target)}`;
  const [state, setState] = useState<PrintQueueLinks & { loading: boolean }>({ ...EMPTY, loading: true });
  useEffect(() => {
    let alive = true;
    const hit = cache.get(target);
    if (!hit || hit.key !== key || Date.now() - hit.at > TTL)
      cache.set(target, { at: Date.now(), key, data: load(target, canAuth) });
    cache.get(target)!.data.then(
      (d) => alive && setState({ ...d, loading: false }),
      (e) => alive && setState({ ...EMPTY, reason: (e as Error).message, loading: false })
    );
    return () => {
      alive = false;
    };
  }, [target, key, canAuth]);
  return state;
}

/**
 * What a print-queue TenantId is: its AUTH tenant (by alias or by id), customer and environments. `own` = the TenantId
 * is the AUTH tenant's id, so the tenant's name is the readable one.
 */
export function linkOf(links: PrintQueueLinks, tenantId: string) {
  const tenant = links.tenants.get(pqKey(tenantId)) ?? null;
  return {
    tenant,
    own: tenant !== null && pqKey(tenant.id) === pqKey(tenantId),
    customer: tenant ? (links.customerNames.get(tenant.customerId) ?? null) : null,
    environments: tenant ? (links.environments.get(tenant.id.toLowerCase()) ?? []) : [],
  };
}

/** Test hook. */
export const resetPrintQueueLinks = () => cache.clear();
